import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';
import { useState, FC } from 'react';
import { useMeasure } from 'react-use';

import { AppSlider } from './AppSlider';

import { Colors } from '@/assets';

export const isNormalValue = (value: number, max: number) => {
    // return true if the value is between 5% -> 95% of the maximum value
    return value > 0.05 * max && value < 0.95 * max;
};

interface IProps {
    unit?: 'cm' | '%';
    min?: number;
    max?: number;
}
export const PrimarySlider: FC<IProps> = ({ unit = '%', min = 0, max = 100 }) => {
    const [value, setValue] = useState<number>(0);
    const [ref, { width }] = useMeasure();
    const [isShowBox, setIsShowBox] = useState<boolean>(true);
    const [isShowButton, setIsShowButton] = useState<boolean>(false);

    const onAdd = () => {
        setValue((prev) => prev + 1);
    };

    const onSub = () => {
        setValue((prev) => prev - 1);
    };

    const length = max - min;

    const minPointBox = -10;
    const maxPointBox = width - 75;
    const widthStepBox = (maxPointBox - minPointBox) / length;
    const boxPosition = widthStepBox * value + minPointBox;

    const minPointDot = width * 0.06;
    const maxPointDot = width * 0.94;
    const widthStepDot = (maxPointDot - minPointDot) / length;
    const dotPosition = widthStepDot * value + minPointDot;

    // when the user moves the mouse away from the input, after 1s the buttons will be hidden instead of immediately hidden.
    let timeShowButton: NodeJS.Timeout;
    return (
        <AppSlider>
            <Box
                onMouseOver={() => {
                    clearTimeout(timeShowButton);
                    setIsShowButton(true);
                }}
                onMouseLeave={() => {
                    timeShowButton = setTimeout(() => {
                        setIsShowButton(false);
                    }, 1000);
                }}
                ref={ref}
                sx={{ position: 'relative', width: 300 }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        width: '100%',
                        height: 2,
                        background: Colors.geyserGrey,
                        ':before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            left: 0,
                            width: dotPosition,
                            height: 2,
                            background: Colors.orangeRed
                        }
                    }}
                ></Box>
                {!isShowBox && (
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 12,
                            height: 12,
                            backgroundColor: Colors.orangeRed,
                            top: 4,
                            background: Colors.white,
                            border: '2px solid',
                            borderColor: Colors.orangeRed,
                            borderRadius: '90px',
                            left: dotPosition
                        }}
                    >
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: -24, left: '50%', transform: 'translateX(-50%)' }}>
                                <Typography sx={{ color: Colors.orangeRed }} variant="subtitle2">
                                    {min + value}
                                    {unit}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
                {isShowBox && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            gap: '2px',
                            left: boxPosition
                        }}
                    >
                        <Box sx={{ position: 'relative' }}>
                            {isShowButton && value !== 0 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: -38,
                                        background: Colors.white,
                                        border: '2px solid',
                                        borderColor: Colors.geyserGrey,
                                        boxSizing: 'border-box',
                                        borderRadius: '90px',
                                        width: 36,
                                        height: 35,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: Colors.vividTangerineOrange,
                                            h5: {
                                                color: Colors.orangeRed
                                            }
                                        }
                                    }}
                                    onClick={onSub}
                                >
                                    <Typography variant="h5" className="far fa-minus"></Typography>
                                </Box>
                            )}
                            <Box
                                sx={{
                                    background: Colors.white,
                                    border: '2px solid',
                                    borderColor: Colors.geyserGrey,
                                    boxSizing: 'border-box',
                                    borderRadius: '30px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 75,
                                    py: 1,
                                    '&:hover': {
                                        borderColor: Colors.vividTangerineOrange
                                    }
                                }}
                            >
                                <Typography sx={{ color: Colors.orangeRed }} variant="subtitle2">
                                    {min + value}
                                    {unit}
                                </Typography>
                            </Box>
                            {isShowButton && value !== length && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 'calc(100% + 2px)',
                                        top: 0,
                                        background: Colors.white,
                                        border: '2px solid #CAD0D0',
                                        borderColor: Colors.geyserGrey,
                                        boxSizing: 'border-box',
                                        borderRadius: '90px',
                                        width: 36,
                                        height: 35,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        zIndex: 1,
                                        '&:hover': {
                                            borderColor: Colors.vividTangerineOrange,
                                            h5: {
                                                color: Colors.orangeRed
                                            }
                                        }
                                    }}
                                    onClick={onAdd}
                                >
                                    <Typography variant="h5" className="far fa-plus"></Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                <Box
                    component="input"
                    onMouseDown={() => {
                        setIsShowButton(false);
                        if (isNormalValue(value, length)) {
                            setIsShowBox(false);
                        }
                    }}
                    onMouseUp={() => {
                        setIsShowBox(true);
                        setIsShowButton(true);
                    }}
                    sx={{
                        '&::-webkit-slider-thumb': {
                            left: boxPosition + 20
                        }
                    }}
                    min={0}
                    max={length}
                    step={1}
                    value={value}
                    onChange={(e: any) => {
                        const value = Number(e.target.value);
                        setValue(value);
                        if (isNormalValue(value, length)) {
                            setIsShowBox(false);
                        } else {
                            setIsShowBox(true);
                        }
                    }}
                    type="range"
                    className="range"
                />
            </Box>
        </AppSlider>
    );
};
